import { useEffect, useState } from 'react'
import { useAuth } from '@/stores/auth'
import { parishesApi } from '@/api/endpoints/parishes'
import AppHeader from '@/components/common/AppHeader'
import RegistrationBlock from '@/components/dashboard/RegistrationBlock'
import RegistrationPdfModal from '@/components/dashboard/modals/RegistrationPdfModal'
import StudentsList from '@/components/dashboard/StudentsList'
import ClassesList from '@/components/dashboard/ClassesList'
import MessageCenter from '@/components/messaging/MessageCenter'
import BirthdayReminders from '@/components/dashboard/BirthdayReminders'
import PersonalNotes from '@/components/dashboard/PersonalNotes'
import SnackWarning from '@/components/dashboard/SnackWarning'
import { motion } from 'framer-motion'
import AppFooter from '@/components/common/AppFooter'

export default function TeacherDashboardPage() {
  const { user } = useAuth()
  const [parishName, setParishName] = useState<string | undefined>(undefined)
  const [sharecode, setSharecode] = useState<string | undefined>(undefined)
  const [showPdfModal, setShowPdfModal] = useState(false)
  // These sections are the teacher's main work area (spec: "prominent"), so they start open.
  // They were rendered without open/onToggle, which left them permanently collapsed.
  const [classesOpen, setClassesOpen] = useState(true)
  const [studentsOpen, setStudentsOpen] = useState(true)

  useEffect(() => {
    const loadParish = async () => {
      if (!user?.parish_id) return
      try {
        const list = await parishesApi.list()
        const active = list.find(p => p.id === user.parish_id)
        if (active) {
          setParishName(active.name)
          setSharecode(active.sharecode)
        }
      } catch {}
    }
    loadParish()
  }, [user?.parish_id])

  if (!user) {
    return <div>Loading...</div>
  }

  return (
    <div className="min-h-screen bg-gray-50 page-transition">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Teacher Dashboard</h1>
          {parishName && sharecode && (
            <div className="mt-2 text-sm text-gray-600">
              <span className="font-semibold">{parishName}</span>
              {' • '}
              <span className="font-mono">Sharecode: {sharecode}</span>
            </div>
          )}
        </div>

        {/* Registration Block */}
        {sharecode && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
          >
            <RegistrationBlock parishName={parishName} sharecode={sharecode} />
            <div className="mt-3">
              <button
                onClick={() => setShowPdfModal(true)}
                className="btn-secondary text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
              >
                Save Registration PDF for Printing
              </button>
            </div>
          </motion.div>
        )}

        {/* Notes Section with Birthday Reminders */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 22, delay: 0.05 }}
          className="mt-6 p-5 rounded-xl border border-gray-200 bg-white shadow-sm hover-raise"
        >
          <h2 className="text-lg font-semibold mb-3">Notes & Reminders</h2>
          <PersonalNotes />
          <BirthdayReminders />
          <SnackWarning />
        </motion.section>

        {/* Assigned Classes - Prominent */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 22, delay: 0.1 }}
          className="mt-6"
        >
          <h2 className="text-xl font-semibold mb-4">Your Assigned Classes</h2>
          <ClassesList open={classesOpen} onToggle={() => setClassesOpen(o => !o)} />
        </motion.div>

        {/* Student List */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 22, delay: 0.15 }}
          className="mt-6"
        >
          <h2 className="text-xl font-semibold mb-4">Students</h2>
          <StudentsList open={studentsOpen} onToggle={() => setStudentsOpen(o => !o)} />
        </motion.div>

        {/* Message Center */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 22, delay: 0.2 }}
          className="mt-6"
        >
          <MessageCenter />
        </motion.div>

        <RegistrationPdfModal
          open={showPdfModal}
          onClose={() => setShowPdfModal(false)}
        />
      </main>
      <AppFooter />
    </div>
  )
}

